/**
 * Boreless Nation — sincronización del descuento de packs
 * (corrección vinculante de packs, 2026-07-26).
 *
 * CONTRATO ECONÓMICO: precio por BLOQUES REPETIDOS con MÁXIMO GLOBAL de 60
 * unidades elegibles por pedido (decisión de Dirección, v5).
 *   precio(n) = 59,95 € × ⌊n/6⌋ + resto(n mod 6)
 *   resto: 0→0 · 1→16,95 · 2→33,90 · 3→39,95 · 4→49,95 · 5→54,95
 *   Ej.: 7 = Pack 6 + 1 · 9 = Pack 6 + Pack 3 · 13 = Pack 6 + Pack 6 + 1.
 * Dentro de 1–60 el cliente elige cualquier cantidad y mezcla libremente
 * variantes. Por encima de 60 el estado es ERROR over_limit: no se aplica ni
 * se calcula ningún tramo nuevo, el checkout queda bloqueado hasta reducir y
 * el carrito NUNCA se recorta automáticamente.
 *
 * UNIDAD ELEGIBLE = línea de carrito cuyo variant_id pertenece a la colección
 * canónica (settings.bn_eligible_collection, emitida por
 * snippets/bn-eligible-data.liquid) y cuyo final_line_price > 0 (excluye
 * tarjetas regalo, líneas gratuitas y promocionales). Sin colección
 * configurada: FAIL CLOSED — 0 unidades elegibles y ningún código propio.
 *
 * TRAMO Y CÓDIGO: el ahorro es 41,75 € por bloque completo más el ahorro del
 * resto, así que 6/7/8 comparten ahorro (tramo 6) y 12/13/14 comparten tramo
 * 12. El tramo canónico es 6×bloques + (resto ≥ 3 ? resto : 0) y cada tramo
 * necesita SU PROPIO código de importe fijo (limitación de la plataforma: un
 * descuento nativo solo puede ser importe fijo o porcentaje). La cobertura por
 * códigos cubre TODO el rango comprable 1–60 (codesMaxUnits = 60), así que
 * dentro del máximo no existe estado UNCOVERED.
 * La autoridad económica definitiva es Shopify/checkout mediante una Discount
 * Function automática (ver EXTERNAL_SERVER_SIDE_REQUIREMENTS_RECONCILED.md).
 *
 * MÁQUINA DE ESTADOS explícita:
 *   IDLE → READING_CART → (APPLYING_CODE → VERIFYING)? → CONFIRMED | ERROR
 * Un HTTP 200 de /{locale}/cart/update.js NUNCA se interpreta como "aplicado":
 * CONFIRMED exige releer /{locale}/cart.js y comprobar que el código del tramo está
 * aplicado y que el importe descontado coincide EXACTAMENTE con el esperado.
 *
 * CÓDIGOS AJENOS (los que no empiezan por PACK ni BNPACK): NUNCA se eliminan.
 * Si Shopify no acepta la combinación, el estado queda en ERROR (code_conflict)
 * y el checkout se bloquea con mensaje; decide el cliente, no el tema. Los
 * códigos propios de OTRO tramo sí se sustituyen por el del tramo vigente.
 *
 * Estado expuesto para el resto del tema:
 *   - atributo data-bn-discount-state en <html>
 *   - evento 'bn:pack-state' (detail = snapshot)
 *   - window.BNPackDiscount = { getSnapshot, ensureSynced }
 */

import { DiscountUpdateEvent } from '@theme/events';

const SOURCE_ID = 'bn-pack-discount';
// Familia propia gestionada por el tema. Incluye BNPACK* SOLO para retirar
// códigos históricos que pudieran quedar aplicados en sesiones antiguas.
const OWN_CODE_PATTERN = /^(PACK|BNPACK)\d+$/i;

const STATE = {
  IDLE: 'idle',
  READING_CART: 'reading_cart',
  APPLYING_CODE: 'applying_code',
  VERIFYING: 'verifying',
  CONFIRMED: 'confirmed',
  ERROR: 'error',
};

let cachedConfig = null;
const readConfig = () => {
  if (cachedConfig) return cachedConfig;
  let parsed = null;
  try {
    const el = document.querySelector('script[data-bn-eligible-config]');
    parsed = el ? JSON.parse(el.textContent) : null;
  } catch (error) {
    parsed = null;
  }
  cachedConfig = {
    configured: Boolean(parsed && parsed.configured),
    maxPaidUnits: Number((parsed && parsed.maxPaidUnits) || 60),
    blockSize: Number((parsed && parsed.blockSize) || 6),
    blockSavingCents: Number((parsed && parsed.blockSavingCents) || 4175),
    remainderSavingsCents: (parsed && parsed.remainderSavingsCents) || { 0: 0, 1: 0, 2: 0, 3: 1090, 4: 1785, 5: 2980 },
    codesMaxUnits: Number((parsed && parsed.codesMaxUnits) || 0),
    tierCodes: (parsed && parsed.tierCodes) || {},
    variantIds: new Set(((parsed && parsed.variantIds) || []).map(String)),
  };
  return cachedConfig;
};

// Rutas locale-aware: en tiendas con varios idiomas las rutas del carrito
// llevan prefijo (/es/cart.js). Shopify.routes.root lo expone.
const cartRoute = (file) => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return `${root}${file}`.replace(/\/{2,}/g, '/');
};

/**
 * Tramo canónico y ahorro esperado para n unidades elegibles.
 * Misma fórmula que snippets/bn-pack-data.liquid (fuente única de importes).
 */
const tierFor = (count, cfg) => {
  const size = cfg.blockSize;
  const blocks = Math.floor(count / size);
  const rem = count % size;
  return blocks * size + (rem >= 3 ? rem : 0);
};

const expectedSavingFor = (count, cfg) => {
  const size = cfg.blockSize;
  const blocks = Math.floor(count / size);
  const rem = count % size;
  return blocks * cfg.blockSavingCents + Number(cfg.remainderSavingsCents[String(rem)] || 0);
};

const snapshot = {
  state: STATE.IDLE,
  reason: null,
  configured: false,
  eligibleCount: 0,
  tier: 0,
  desiredCode: null,
  // true cuando hay ahorro contractual pero NINGÚN código lo cubre: el
  // carrito muestra el precio real y el checkout sigue abierto.
  uncovered: false,
  expectedSavingCents: 0,
  appliedPackCodes: [],
  otherCodes: [],
  verifiedSavingCents: null,
  lastSyncAt: null,
};

const emit = (state, patch) => {
  snapshot.state = state;
  if (patch) Object.assign(snapshot, patch);
  document.documentElement.setAttribute('data-bn-discount-state', state);
  document.dispatchEvent(new CustomEvent('bn:pack-state', { detail: { ...snapshot } }));
};

const fetchCart = async () => {
  const response = await fetch(cartRoute('cart.js'), {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('cart_read_failed');
  return response.json();
};

// Unidades elegibles DE PAGO: variante en la colección canónica y línea con
// importe > 0. cart.item_count NO se usa nunca (BN-001).
const countEligible = (cart, cfg) => {
  if (!cfg.configured) return 0;
  return (cart.items || []).reduce((total, item) => {
    if (item.gift_card) return total;
    if (!cfg.variantIds.has(String(item.variant_id))) return total;
    if (!(Number(item.final_line_price) > 0)) return total;
    return total + (Number(item.quantity) || 0);
  }, 0);
};

/**
 * Reúne los descuentos de CÓDIGO realmente aplicados y su importe, mirando las
 * DOS ubicaciones donde Shopify los coloca en /cart.js:
 *   - cart.cart_level_discount_applications  → descuentos de PEDIDO.
 *   - items[].line_level_discount_allocations → descuentos de PRODUCTO.
 *
 * Los 40 códigos de packs son «Amount off products» (discountClasses: PRODUCT),
 * así que Shopify los asigna POR LÍNEA y NUNCA aparecen a nivel de pedido. Leer
 * solo cart_level_discount_applications (como hacía la versión anterior) hacía
 * que un pack correctamente descontado se viera como `not_applied` y el checkout
 * quedara bloqueado. Se suman las asignaciones de todas las líneas por código.
 *
 * Todos los importes de /cart.js vienen en CENTAVOS enteros.
 */
const collectAppliedCodeDiscounts = (cart) => {
  const amountByCode = new Map(); // TÍTULO_MAYÚSCULAS → centavos
  const titleByCode = new Map(); // TÍTULO_MAYÚSCULAS → título tal cual
  const record = (rawTitle, cents) => {
    const title = String(rawTitle || '').trim();
    if (!title) return;
    const up = title.toUpperCase();
    if (!titleByCode.has(up)) titleByCode.set(up, title);
    amountByCode.set(up, (amountByCode.get(up) || 0) + (Number(cents) || 0));
  };
  (cart.cart_level_discount_applications || []).forEach((application) => {
    if (application.type !== 'discount_code') return;
    record(application.title, application.total_allocated_amount);
  });
  (cart.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      const application = allocation.discount_application || {};
      if (application.type !== 'discount_code') return;
      record(application.title, allocation.amount);
    });
  });
  return {
    titles: Array.from(titleByCode.values()),
    amountFor: (code) => amountByCode.get(String(code || '').toUpperCase()) || 0,
  };
};

const analyse = (cart, cfg) => {
  const eligibleCount = countEligible(cart, cfg);
  const applied = collectAppliedCodeDiscounts(cart);
  const appliedCodes = applied.titles;
  const appliedPackCodes = appliedCodes.filter((code) => OWN_CODE_PATTERN.test(code));
  const otherCodes = appliedCodes.filter((code) => !OWN_CODE_PATTERN.test(code));
  // Máximo global (60): por encima no se calcula tramo nuevo ni se aplica
  // código; se retira el que hubiera y el checkout queda bloqueado hasta que
  // el cliente reduzca. NUNCA se recorta el carrito desde aquí.
  const overLimit = eligibleCount > cfg.maxPaidUnits;
  const tier = overLimit ? 0 : tierFor(eligibleCount, cfg);
  const contractualSaving = overLimit ? 0 : expectedSavingFor(eligibleCount, cfg);
  const desiredCode = tier >= 3 ? cfg.tierCodes[String(tier)] || null : null;
  // Dentro de 1–60 la cobertura de códigos es completa, así que `uncovered`
  // solo puede darse si Admin reduce codesMaxUnits por debajo del máximo.
  const uncovered = !overLimit && tier >= 3 && !desiredCode;
  const expectedSavingCents = desiredCode ? contractualSaving : 0;
  const appliedSavingCents = desiredCode ? applied.amountFor(desiredCode) : 0;
  const desiredApplied = Boolean(
    desiredCode
    && appliedPackCodes.length === 1
    && appliedPackCodes[0].toUpperCase() === desiredCode.toUpperCase()
  );
  // Escritura necesaria solo si el CONJUNTO de códigos propios no es el deseado.
  // Un importe incorrecto con el código correcto es un problema de Admin: se
  // señala como ERROR sin reescritura (reintentar no lo arreglaría).
  const needsWrite = desiredCode ? !desiredApplied : appliedPackCodes.length > 0;
  return {
    eligibleCount,
    overLimit,
    tier,
    uncovered,
    contractualSaving,
    desiredCode,
    expectedSavingCents,
    appliedSavingCents,
    appliedPackCodes,
    otherCodes,
    desiredApplied,
    needsWrite,
  };
};

// Cierra el ciclo con el veredicto de un análisis sobre datos FRESCOS.
const settle = (a) => {
  const base = {
    eligibleCount: a.eligibleCount,
    tier: a.tier,
    uncovered: a.uncovered,
    expectedSavingCents: a.contractualSaving,
    desiredCode: a.desiredCode,
    appliedPackCodes: a.appliedPackCodes,
    otherCodes: a.otherCodes,
    lastSyncAt: Date.now(),
  };
  if (a.overLimit) {
    // Por encima del máximo global: sin código y checkout bloqueado hasta
    // que el cliente reduzca. El carrito NO se toca.
    emit(STATE.ERROR, { ...base, reason: 'over_limit', verifiedSavingCents: null });
    return;
  }
  if (!a.desiredCode) {
    if (a.appliedPackCodes.length === 0) {
      // 1–2 unidades no necesitan código. `uncovered` solo aparecería si
      // Admin bajara la cobertura por debajo del máximo global.
      emit(STATE.CONFIRMED, { ...base, reason: a.uncovered ? 'uncovered' : null, verifiedSavingCents: 0 });
    } else {
      // Quedó un código propio que no debería estar (la retirada no cuajó).
      emit(STATE.ERROR, { ...base, reason: 'stale_pack_code', verifiedSavingCents: null });
    }
    return;
  }
  if (a.desiredApplied) {
    if (a.expectedSavingCents > 0 && a.appliedSavingCents === a.expectedSavingCents) {
      emit(STATE.CONFIRMED, { ...base, reason: null, verifiedSavingCents: a.appliedSavingCents });
    } else {
      // Código aplicado pero el importe NO coincide con el tramo (Admin mal
      // configurado). Nunca CONFIRMED sin igualdad exacta.
      emit(STATE.ERROR, { ...base, reason: 'wrong_amount', verifiedSavingCents: null });
    }
    return;
  }
  emit(STATE.ERROR, {
    ...base,
    reason: a.otherCodes.length > 0 ? 'code_conflict' : 'not_applied',
    verifiedSavingCents: null,
  });
};

let running = false;
let pendingSync = false;
let waiters = [];

const resolveWaiters = () => {
  const settled = waiters;
  waiters = [];
  settled.forEach((resolve) => resolve({ ...snapshot }));
};

const syncPackDiscount = async () => {
  if (running) {
    pendingSync = true;
    return;
  }
  running = true;
  const cfg = readConfig();
  snapshot.configured = cfg.configured;
  snapshot.codesMaxUnits = cfg.codesMaxUnits;
  snapshot.maxPaidUnits = cfg.maxPaidUnits;

  try {
    emit(STATE.READING_CART);
    const cart = await fetchCart();
    const first = analyse(cart, cfg);

    if (!first.needsWrite) {
      settle(first);
      return;
    }

    emit(STATE.APPLYING_CODE, { eligibleCount: first.eligibleCount, desiredCode: first.desiredCode });
    const codes = first.desiredCode
      ? [...first.otherCodes, first.desiredCode]
      : [...first.otherCodes];
    const updateResponse = await fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!updateResponse.ok) throw new Error('cart_update_failed');
    await updateResponse.json();

    // VERIFYING: relectura independiente. El 200 anterior NO demuestra nada.
    emit(STATE.VERIFYING);
    const fresh = await fetchCart();
    const second = analyse(fresh, cfg);
    if (second.eligibleCount !== first.eligibleCount) {
      // El carrito cambió mientras aplicábamos: otro ciclo con datos frescos.
      pendingSync = true;
    }
    settle(second);

    // Mecanismo nativo del tema: component-cart-items re-renderiza drawer y
    // página de carrito. El listener de este script ignora sourceId propio:
    // máximo UN intento por cambio externo, nunca un bucle.
    document.dispatchEvent(new DiscountUpdateEvent(fresh, SOURCE_ID));
  } catch (error) {
    emit(STATE.ERROR, { reason: 'network', verifiedSavingCents: null, lastSyncAt: Date.now() });
  } finally {
    running = false;
    if (pendingSync) {
      pendingSync = false;
      syncPackDiscount();
    } else {
      resolveWaiters();
    }
  }
};

let syncTimeout = null;
const scheduleSync = () => {
  if (syncTimeout) clearTimeout(syncTimeout);
  syncTimeout = setTimeout(syncPackDiscount, 350);
};

/**
 * API para bn-checkout-gate.js y el builder: fuerza un ciclo completo AHORA y
 * resuelve cuando la máquina queda asentada (CONFIRMED o ERROR).
 */
const ensureSynced = () => new Promise((resolve) => {
  waiters.push(resolve);
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
  if (running) {
    pendingSync = true;
  } else {
    syncPackDiscount();
  }
});

window.BNPackDiscount = {
  getSnapshot: () => ({ ...snapshot }),
  ensureSynced,
};
document.dispatchEvent(new CustomEvent('bn:pack-discount-ready', { bubbles: true }));

document.addEventListener('cart:update', scheduleSync);
// Guardia anti-bucle: los discount:update generados por este propio script no
// reprograman la sincronización. Los cambios externos (cantidades u otros
// códigos, con otro sourceId) sí lanzan una nueva sincronización.
document.addEventListener('discount:update', (event) => {
  if (event && event.detail && event.detail.sourceId === SOURCE_ID) return;
  scheduleSync();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSync);
} else {
  scheduleSync();
}
